import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../../contexts/NavigationGuardContext";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ActionConfirmationDialog from "../../components/ActionConfirmationDialog";
import InteractiveElement from "../../components/InteractiveElement/InteractiveElement";
import { anchorWidthSx, rewampDashboardAccess } from "./RewampUtils";
import GlobalSearchInput from "./GlobalSearch/GlobalSearchInput";
import RewampComingSoonDialog from "./dialog/RewampComingSoonDialog";
import { REWAMP_NAV_HEIGHT } from "../layouts/navMetrics";
import { useAppbarRoleModules } from "./hooks/useAppbarRoleModules";
import isStandalone from "../../Utils/isStandalone";

const BP_RANK = { md: 0, lg: 1, xl: 2 };

// Styling for the responsive "More" overflow menu (an overflow utility, not a nav
// submenu — every nav item itself is flat single-click; see appbar-roles.md).
const moreMenuPaperSx = {
  bgcolor: "primary.dark",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  mt: 0.5,
  // Width floor comes from the trigger via anchorWidthSx({ grow:true }), not a
  // hardcoded value — see the More <Menu> below.
  boxShadow: "0 8px 24px rgba(0,0,0,0.24)",
};

const moreMenuItemSx = (active) => ({
  color: active ? "background.white" : "background.tertiaryGreen",
  fontWeight: active ? 700 : 500,
  fontSize: "0.875rem",
  bgcolor: active ? "rgba(255,255,255,0.1)" : "transparent",
  borderRadius: "4px",
  mx: 0.5,
  my: 0.25,
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)", color: "background.white" },
});

export const RewampMainNav = React.memo(({ handleLogout }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const location = useLocation();

  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  const loggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
  const userInfo = useSelector((state) => state.profileData);
  const name = userInfo?.name || "User";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Dashboard-admin roles (STADMIN / DIRECTOR / SECRETARY / JEEVIKA — the same
  // ROLE_GROUPS.DASHBOARD_ADMIN set that gates /dashboards) don't get the farmer
  // global search: hide both the inline nav search and the mobile drawer entry.
  const hideGlobalSearch = rewampDashboardAccess();

  const roleNavItems = useAppbarRoleModules();
  // Logged-out installed app: every item lands on / or a gated route, both of which bounce to /login.
  const navItems = isStandalone() && !loggedIn ? [] : roleNavItems;

  // Active-state resolution, covering both route items and in-page landmarks:
  //   • landmark item (path:null, key:"#id") → active when the URL hash matches
  //     its key, so clicking "Schemes"/"Advisories"/"Help" lights that button.
  //   • route item → active when the pathname matches AND no landmark hash is
  //     selected, so Home doesn't stay lit while a #section anchor is active.
  //
  // Matching is exact (with trailing-slash + nested-route tolerance), NOT a
  // loose `includes`: Home's path is the contextPath root (`${contextPath}/`),
  // and every route lives under it, so `includes` lit Home on /schemes, /track,
  // etc. The root target therefore matches only its own page; deeper routes also
  // match nested children on a segment boundary (so /grm/inbox keeps its parent
  // lit, but /track no longer false-matches /track-status).
  const isItemActive = (item) => {
    if (!item?.path) return Boolean(item?.key) && location.hash === item.key;
    if (location.hash) return false;
    const norm = (p) => {
      const s = (p || "").replace(/\/{1,100}$/, "");
      return s === "" ? "/" : s;
    };
    const current = norm(location.pathname);
    const target = norm(item.path);
    const root = norm(window.contextPath || "/");
    if (target === root) return current === root;
    return current === target || current.startsWith(`${target}/`);
  };

  // Every nav item is a single-click navigation — no dropdowns.
  // Anchors (path: null) navigate to the landing page with a hash target so
  // the section scroll fires whether the user is already on /home or coming
  // from a different route. Disabled items skip onClick entirely (no-op).
  const goTo = (path, key) => {
    setDrawerOpen(false);
    setMoreAnchor(null);
    setProfileAnchor(null);
    if (path === null && key) {
      const homePath = loggedIn ? `${window.contextPath}/home` : `${window.contextPath}/`;
      navigate(`${homePath}${key}`);
    } else if (path) {
      navigate(path);
    }
  };

  // Items flagged `comingSoon: true` skip navigation and open the placeholder
  // dialog instead. Disabled items remain a no-op (tooltip-only).
  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.comingSoon) {
      setDrawerOpen(false);
      setMoreAnchor(null);
      setComingSoonOpen(true);
      return;
    }
    goTo(item.path, item.key);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    handleLogout?.();
  };

  // Split items into inline vs More based on current breakpoint
  const inlineThreshold = isXl ? "xl" : isLg ? "lg" : "md";
  const inlineItems = navItems.filter(
    (item) => BP_RANK[item.showFrom ?? "md"] <= BP_RANK[inlineThreshold]
  );
  const moreItems = navItems.filter(
    (item) => BP_RANK[item.showFrom ?? "md"] > BP_RANK[inlineThreshold]
  );

  const navBtnSx = {
    color: "background.tertiaryGreen",
    borderRadius: "8px",
    px: 1.5,
    py: 0.75,
    minHeight: "30px",
    fontWeight: 600,
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
    lineHeight: 1,
    "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "background.white" },
  };

  const krishiLogoSrc = `${process.env.PUBLIC_URL}/assets/RewampBiharKrishiLogo.png`;

  return (
    <>
      {/* ── Desktop / Tablet nav bar ─────────────────────────────── */}
      <Box
        id="rewamp-main-nav"
        component="nav"
        aria-label={t("MAIN_NAVIGATION")}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          bgcolor: "primary.main",
          borderBottom: "2.4px solid",
          borderColor: "accent.butterscotch",
          px: { xs: 2, md: 4, lg: 9 },
          minHeight: `${REWAMP_NAV_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Inline nav links — hidden on mobile. Yields its free space to the
            search bar when it opens (flexGrow → 0). */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: "2px",
            flexGrow: searchOpen ? 0 : 1,
            minWidth: 0,
          }}
        >
          {inlineItems.map((item) => {
            const active = isItemActive(item);
            const isDisabled = Boolean(item.disabled);
            const button = (
              <Button
                onClick={isDisabled ? undefined : () => handleItemClick(item)}
                aria-disabled={isDisabled || undefined}
                sx={{
                  ...navBtnSx,
                  color: active ? "background.white" : "background.tertiaryGreen",
                  bgcolor: active ? "rgba(255,255,255,0.1)" : "transparent",
                  ...(isDisabled && {
                    opacity: 0.55,
                    cursor: "not-allowed",
                    "&:hover": { bgcolor: "transparent" },
                  }),
                }}
              >
                {t(item.title)}
              </Button>
            );
            if (isDisabled && item.tooltip) {
              return (
                <Tooltip
                  key={item.title}
                  title={t(item.tooltip)}
                  arrow
                  disableInteractive
                >
                  {button}
                </Tooltip>
              );
            }
            return React.cloneElement(button, { key: item.title });
          })}

          {/* More overflow button */}
          {moreItems.length > 0 && (
            <Button
              id="more-nav-btn"
              aria-controls={moreAnchor ? "more-nav-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(moreAnchor)}
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              endIcon={
                <KeyboardArrowDownIcon
                  aria-hidden="true"
                  sx={{
                    fontSize: "16px !important",
                    ml: -0.25,
                    transition: "transform 0.2s",
                    transform: moreAnchor ? "rotate(180deg)" : "none",
                  }}
                />
              }
              sx={{
                ...navBtnSx,
                bgcolor: Boolean(moreAnchor) ? "rgba(255,255,255,0.1)" : "transparent",
                color: Boolean(moreAnchor) ? "background.white" : "background.tertiaryGreen",
              }}
            >
              {t("More")}
            </Button>
          )}
        </Box>

        {/* Hamburger — mobile only */}
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            display: { xs: "flex", md: "none" },
            color: "background.white",
            bgcolor: "primary.main",
            p: 0.75,
            borderRadius: "8px",
            "&:hover": { bgcolor: "primary.dark" },
          }}
          aria-label={t("OPEN_NAVIGATION")}
        >
          <MenuIcon />
        </IconButton>

        {/* Right: Search + Login / Profile. Grows when search opens so the bar
            can claim the space the nav links released. gap:2 (16px) matches the
            Figma "Right: search + login" frame — the fixed gap the search bar
            keeps from the profile section as it extends leftward. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexGrow: searchOpen ? 1 : 0,
            flexShrink: searchOpen ? 1 : 0,
            minWidth: 0,
          }}
        >
          {!hideGlobalSearch && (
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                flexGrow: searchOpen ? 1 : 0,
                minWidth: 0,
              }}
            >
              <GlobalSearchInput onOpenChange={setSearchOpen} />
            </Box>
          )}

          {loggedIn ? (
            <InteractiveElement onClick={(e) => setProfileAnchor(e.currentTarget)}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  color: "background.white",
                  // Match the 38px search bar / login button so the search bar
                  // and profile box share the same height and baseline.
                  height: "38px",
                  px: 1.5,
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.4)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                <AccountCircleIcon sx={{ fontSize: "22px", color: "background.white" }} />
                <Box
                  component="span"
                  sx={{
                    display: { xs: "none", sm: "inline" },
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    maxWidth: 100,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </Box>
                <ExpandMoreIcon sx={{ fontSize: "16px" }} />
              </Box>
            </InteractiveElement>
          ) : (
            <Button
              startIcon={
                <PersonOutlineIcon
                  aria-hidden="true"
                  sx={{ color: "background.white", fontSize: "16px !important" }}
                />
              }
              onClick={() => goTo(`${window.contextPath}/login`)}
              sx={{
                height: "38px",
                // Flexible width (was a fixed 140px): lets the label grow when
                // the Text-Spacing a11y option adds letter-spacing, and for
                // longer Hindi/translated labels — so text never clips the box.
                width: "auto",
                minWidth: { sm: "140px" },
                px: { xs: 1.5, sm: 2 },
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "background.white",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: "0.875rem",
                textTransform: "none",
                justifyContent: "center",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.6)",
                },
              }}
            >
              {t("REWAMP_NAV_LOGIN_REGISTER")}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── More overflow dropdown — flat single-click items ──────── */}
      <Menu
        id="more-nav-menu"
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        disableScrollLock
        MenuListProps={{ "aria-labelledby": "more-nav-btn" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { ...moreMenuPaperSx, ...anchorWidthSx(moreAnchor, { grow: true }) },
          },
        }}
      >
        {moreItems.map((item) => {
          const isDisabled = Boolean(item.disabled);
          const menuItem = (
            <MenuItem
              onClick={isDisabled ? undefined : () => handleItemClick(item)}
              aria-disabled={isDisabled || undefined}
              sx={{
                ...moreMenuItemSx(isItemActive(item)),
                ...(isDisabled && {
                  opacity: 0.55,
                  cursor: "not-allowed",
                  "&:hover": { bgcolor: "transparent" },
                }),
              }}
            >
              {t(item.title)}
            </MenuItem>
          );
          if (isDisabled && item.tooltip) {
            return (
              <Tooltip
                key={item.title}
                title={t(item.tooltip)}
                placement="left"
                arrow
                disableInteractive
              >
                {menuItem}
              </Tooltip>
            );
          }
          return React.cloneElement(menuItem, { key: item.title });
        })}
      </Menu>

      {/* ── Profile dropdown ─────────────────────────────────────── */}
      <Menu
        id="profile-menu"
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // mt:1 → 8px gap below the profile box. grow:true → trigger width is a
        // floor (minWidth) but the paper expands to fit longer item labels, so
        // translations like the Hindi "Edit Profile" (प्रोफ़ाइल संपादित करें)
        // never clip against the menu edge. Same pattern as the More menu above.
        slotProps={{ paper: { sx: { mt: 1, ...anchorWidthSx(profileAnchor, { grow: true }) } } }}
      >
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            navigate(`${window.contextPath}/edit-profile`);
          }}
        >
          <AccountBoxOutlinedIcon sx={{ mr: 1 }} />
          {t("EDIT_PROFILE")}
        </MenuItem>
        <MenuItem
          onClick={() => { setProfileAnchor(null); setLogoutDialogOpen(true); }}
          sx={{ color: "error.main" }}
        >
          <LogoutOutlinedIcon color="error" sx={{ mr: 1 }} />
          {t("ACTION_LOGOUT")}
        </MenuItem>
      </Menu>

      {/* ── Mobile drawer — flat single-click items ───────────────── */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "280px" } }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            component="img"
            src={krishiLogoSrc}
            alt={t("BIHAR_KRISHI_LOGO")}
            sx={{ width: 56, height: 40, objectFit: "contain" }}
          />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label={t("CLOSE_NAVIGATION")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="nav" aria-label={t("MAIN_NAVIGATION")}>
          {navItems.map((item) => {
            const active = isItemActive(item);
            const isDisabled = Boolean(item.disabled);
            return (
              <MenuItem
                key={item.title}
                onClick={isDisabled ? undefined : () => handleItemClick(item)}
                aria-disabled={isDisabled || undefined}
                sx={{
                  py: 1.5,
                  px: 2.5,
                  fontWeight: active ? 700 : 400,
                  color: active ? "primary.interactive" : "text.primary",
                  borderLeft: active
                    ? `3px solid ${theme.palette.primary.interactive}`
                    : "3px solid transparent",
                  ...(isDisabled && {
                    opacity: 0.55,
                    cursor: "not-allowed",
                  }),
                }}
              >
                {t(item.title)}
                {isDisabled && item.tooltip && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: "0.75rem",
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    ({t(item.tooltip)})
                  </Box>
                )}
              </MenuItem>
            );
          })}
        </Box>

        <Box
          sx={{ mt: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          {!hideGlobalSearch && (
            <Button
              variant="secondary"
              startIcon={<SearchIcon aria-hidden="true" />}
              fullWidth
              onClick={() => goTo(`${window.contextPath}/search`)}
            >
              {t("SEARCH")}
            </Button>
          )}
          {loggedIn ? (
            <>
              <Button
                variant="secondary"
                startIcon={<AccountBoxOutlinedIcon aria-hidden="true" />}
                fullWidth
                onClick={() => {
                  setDrawerOpen(false);
                  navigate(`${window.contextPath}/edit-profile`);
                }}
              >
                {t("EDIT_PROFILE")}
              </Button>
              <Button
                variant="primary"
                startIcon={<LogoutOutlinedIcon aria-hidden="true" />}
                fullWidth
                onClick={() => { setDrawerOpen(false); setLogoutDialogOpen(true); }}
              >
                {t("ACTION_LOGOUT")}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              startIcon={<PersonOutlineIcon aria-hidden="true" />}
              fullWidth
              onClick={() => goTo(`${window.contextPath}/login`)}
            >
              {t("REWAMP_NAV_LOGIN_REGISTER")}
            </Button>
          )}
        </Box>
      </Drawer>

      <ActionConfirmationDialog
        open={logoutDialogOpen}
        setOpen={setLogoutDialogOpen}
        onContinueClick={handleLogoutConfirm}
        actionHeader="COMMON_LOGOUT"
        warningText="COMMON_LOGOUT_WARNING"
      />

      <RewampComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        title="REWAMP_COMING_SOON_KNOWLEDGE_TITLE"
      />
    </>
  );
});

export default RewampMainNav;
